import React, { FC, ReactElement, useContext, useMemo } from 'react'
import IconButton, { IconButtonType } from '../../Common/Components/IconButton'
import { IconName } from '../../Common/Components/Icon'
import { UserTypes } from '../../SelfProvisioning/Constants/Constants'
import CLanguageHandler from '../../../NonReact/Languages/LanguageHandler'
import { LanguageContext, PromptContext } from '../../Common/Contexts'
import { UsersByExtension } from '../../../../i18n'
import { PromptDisplayType } from '../../Common/Contexts/PromptContext'
import { LanguageUtils } from '../../Common/Utils/LanguageUtils'
import { TelSysTypes } from '../../../NonReact/constants/TelephonyServerTypes'

type Props = {
    addAllUsers: (userType: string, shouldSendWelcomeEmail: boolean) => Promise<void>
    telSysType?: TelSysTypes
    historicOnly: boolean
    isAdminProvisioningUser: boolean
    availableExtensions: number
}

export const AddAllUsers: FC<Props> = ({
    addAllUsers,
    historicOnly,
    telSysType,
    isAdminProvisioningUser,
    availableExtensions
}: Props): ReactElement => {
    const language: CLanguageHandler = useContext(LanguageContext) as CLanguageHandler

    const { displayPromptPromise }: PromptDisplayType = useContext(PromptContext)

    const addAllUsersUserType: string = useMemo((): string => {
        if (isAdminProvisioningUser) return historicOnly ? UserTypes.ESSENTIALS : UserTypes.CALL_CENTRE_AGENT

        return historicOnly ? UserTypes.ESSENTIALS : UserTypes.PROFESSIONAL
    }, [isAdminProvisioningUser, historicOnly])

    const addAllUserTypeLanguage: string = useMemo((): string => {
        if (isAdminProvisioningUser)
            return historicOnly
                ? UsersByExtension.UsersByExtension_UserType_ESSENTIALS
                : UsersByExtension.UsersByExtension_UserType_CALL_CENTRE_AGENT

        return historicOnly
            ? UsersByExtension.UsersByExtension_UserType_ESSENTIALS
            : UsersByExtension.UsersByExtension_UserType_PROFESSIONAL
    }, [isAdminProvisioningUser, historicOnly])

    const addAllUserTypeTitleLanguage: string = useMemo((): string => {
        if (isAdminProvisioningUser)
            return historicOnly
                ? UsersByExtension.UsersByExtension_UserType_ESSENTIALS
                : UsersByExtension.UsersByExtension_UserType_CALL_CENTRE_AGENT

        return historicOnly
            ? UsersByExtension.UsersByExtension_UserType_ESSENTIALS
            : UsersByExtension.UsersByExtension_UserType_PROFESSIONAL
    }, [isAdminProvisioningUser, historicOnly])

    const addAllPrompt = (): void => {
        displayPromptPromise(
            LanguageUtils.formatLanguageEntry(
                language.getInterpolated(
                    UsersByExtension.AddAllUsers_PromptMessage,
                    language.getWithTelSysCheck(addAllUserTypeLanguage, telSysType)
                )
            ),
            language.get(UsersByExtension.AddAllUsers_Confirm),
            language.get(UsersByExtension.AddAllUsers_Cancel),
            {
                checkMessage: LanguageUtils.formatLanguageEntry(
                    language.get(UsersByExtension.AddAllUsers_Send_Welcome_Email_Checkbox_Text)
                ),
                defaultChecked: true
            }
        ).then(
            async (shouldSendWelcomeEmail: Definable<boolean>): Promise<void> =>
                addAllUsers(addAllUsersUserType, shouldSendWelcomeEmail ?? true)
        )
    }

    return (
        <div className='add-all-extensions'>
            <div className='add-all-icon-container'>
                <IconButton
                    type={IconButtonType.BUTTON}
                    iconName={IconName.ADD_ALL}
                    isEnabled={availableExtensions > 0}
                    onClick={(): void => addAllPrompt()}
                    title={language.getInterpolated(
                        UsersByExtension.AddAllUsers_Button_Title_Interpolated,
                        language.getWithTelSysCheck(addAllUserTypeTitleLanguage, telSysType)
                    )}
                />
            </div>
        </div>
    )
}
